import React, { useState, useEffect, useRef } from 'react';
import { DonationService } from '../../services/DonationService';
import { ProjectService } from '../../services/ProjectService';
import { DonorProfileService } from '../../services/DonorProfileService';
import { OrganizationService } from '../../services/OrganizationService';
import { useOrganization } from '../../contexts/OrganizationContext';
import { TopBar } from '../../components/TopBar/TopBar';
import { Donation } from '../../shared/models/Donation';
import { Project } from '../../shared/models/Project';
import { DonorProfile } from '../../services/DonorProfileService';
import { Organization } from '../../contexts/OrganizationContext';
import { showError, logError, logInfo } from '../../utils/errorHandler';
import { formatFullDateTime } from '../../utils/dateFormat';
import * as WechatAuthService from '../../services/WechatAuthService';
import { initWxJSSDK } from '../../services/WechatPayService';
import './CertificateView.css';

/**
 * 获取完整的图片URL
 * 如果URL已经是完整的HTTP/HTTPS URL，直接返回
 * 如果是相对路径，添加API基础URL前缀
 */
const getImageUrl = (url?: string): string | undefined => {
  if (!url) return undefined;
  
  // 如果已经是完整的URL（http:// 或 https://），直接返回
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }
  
  // 如果是相对路径，添加API基础URL
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : 'http://localhost:8080/api');
  // 图片文件通常在 /api 的同级目录，或者直接在根目录
  // 根据实际情况调整，这里假设图片在根目录
  const baseUrl = apiBaseUrl.replace('/api', '');
  return url.startsWith('/') ? `${baseUrl}${url}` : `${baseUrl}/${url}`;
};

interface CertificateViewProps {
  donationId: string;
  onBack: () => void;
}

/**
 * 证书查看页面
 */
export const CertificateView: React.FC<CertificateViewProps> = ({ donationId, onBack }) => {
  const { organization: contextOrg } = useOrganization();
  const [organization, setOrganization] = useState<Organization | null>(contextOrg);
  const [donation, setDonation] = useState<Donation | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [donorProfile, setDonorProfile] = useState<DonorProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [certificateContent, setCertificateContent] = useState<string>('');
  const certificateRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadCertificateData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donationId, contextOrg?.id]);

  const loadCertificateData = async () => {
    setLoading(true);
    try {
      const openId = WechatAuthService.getOpenId();
      if (!openId) {
        showError('未获取到微信授权信息', 'CertificateView.loadCertificateData');
        setLoading(false);
        return;
      }

      // 1. 获取捐赠详情（包含累计金额）
      // 根据当前机构类型传入正确的参数，以便后端正确计算累计金额
      const donationData = await DonationService.getDonationById(
        donationId,
        contextOrg?.type,
        contextOrg?.id
      );
      setDonation(donationData);

      // 2. 获取项目信息
      if (donationData.projectId) {
        const projectData = await ProjectService.getProjectById(donationData.projectId);
        setProject(projectData);
      }

      // 3. 获取捐赠人信息
      if (donationData.donorOpenId) {
        const profile = await DonorProfileService.getProfile(donationData.donorOpenId);
        setDonorProfile(profile);
      }

      // 4. 获取机构完整信息（包含证书配置）
      if (contextOrg?.id) {
        try {
          const orgData = await OrganizationService.getOrganizationById(contextOrg.id);
          setOrganization(orgData);
        } catch (error) {
          logError('[CertificateView] Failed to load organization details', error);
          // 如果加载失败，使用 context 中的机构信息
          setOrganization(contextOrg);
        }
      } else {
        setOrganization(contextOrg);
      }

      // 5. 生成证书内容（在 organization 设置后）
      if (donationData) {
        generateCertificateContent(donationData);
      }
    } catch (error) {
      logError('[CertificateView] Failed to load certificate data', error);
      showError('加载证书数据失败', 'CertificateView.loadCertificateData');
    } finally {
      setLoading(false);
    }
  };

  const generateCertificateContent = (donationData: Donation) => {
    const currentOrg = organization || contextOrg;
    if (!currentOrg?.certContentTemplate) {
      setCertificateContent('证书模板未配置');
      return;
    }

    // 获取替换数据
    const charity = currentOrg.name || '慈善机构';
    const projectName = donationData.projectName || project?.title || '项目';
    const donationName = donorProfile?.name || donationData.donorNickname || donationData.donorName || '爱心人士';
    const money = donationData.donationType === 'MONTHLY' && donationData.totalDonationAmount
      ? donationData.totalDonationAmount
      : donationData.amount;
    const moneySum = donationData.totalDonationAmountSum || 0;
    const donationTime = donationData.paymentTime
      ? formatFullDateTime(donationData.paymentTime)
      : formatFullDateTime(donationData.createTime);

    // 替换占位符
    let content = currentOrg.certContentTemplate
      .replace(/\{\$charity\}/g, charity)
      .replace(/\{\$projectName\}/g, projectName)
      .replace(/\{\$donationName\}/g, donationName)
      .replace(/\{\$money\}/g, money.toFixed(2))
      .replace(/\{\$moneySum\}/g, moneySum.toFixed(2))
      .replace(/\{\$donationTime\}/g, donationTime)
      .replace(/\^/g, '<br/>'); // 强制换行

    setCertificateContent(content);
  };

  /**
   * 检测是否是移动设备
   */
  const isMobile = (): boolean => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  };

  /**
   * 检测是否是微信浏览器
   */
  const isWechatBrowser = (): boolean => {
    return /MicroMessenger/i.test(navigator.userAgent);
  };

  /**
   * 使用微信 JS-SDK 保存图片到相册
   */
  const saveImageWithWechatSDK = async (imageUrl: string): Promise<void> => {
    try {
      // 尝试初始化微信 JS-SDK
      try {
        await initWxJSSDK();
      } catch (error) {
        logError('[CertificateView] Failed to init WeChat JS-SDK', error);
        // 如果初始化失败，继续尝试使用（可能已经初始化过了）
      }

      if (typeof (window as any).wx === 'undefined') {
        throw new Error('微信 JS-SDK 未加载');
      }

      const wx = (window as any).wx;
      
      // 使用微信 JS-SDK 的 previewImage 接口，用户可以长按保存
      return new Promise((resolve, reject) => {
        wx.previewImage({
          current: imageUrl,
          urls: [imageUrl],
          success: () => {
            logInfo('[CertificateView] WeChat previewImage success');
            // 提示用户长按保存
            alert('请长按图片保存到相册');
            resolve();
          },
          fail: (err: any) => {
            logError('[CertificateView] WeChat previewImage failed', err);
            reject(new Error('预览图片失败'));
          }
        });
      });
    } catch (error) {
      logError('[CertificateView] Failed to use WeChat SDK', error);
      throw error;
    }
  };

  /**
   * 保存图片到移动设备
   */
  const saveImageToMobile = async (imageUrl: string): Promise<void> => {
    // 如果是微信浏览器，尝试使用微信 JS-SDK
    if (isWechatBrowser()) {
      try {
        await saveImageWithWechatSDK(imageUrl);
        return;
      } catch (error) {
        logError('[CertificateView] Failed to save with WeChat SDK, fallback to manual save', error);
        // 如果微信 SDK 失败，继续使用通用方法
      }
    }

    // 通用方法：创建临时图片元素，引导用户长按保存
    const img = document.createElement('img');
    img.src = imageUrl;
    img.style.cssText = 'position: fixed; top: 0; left: 0; width: 100%; height: 100%; z-index: 9999; object-fit: contain; background: rgba(0,0,0,0.8);';
    img.onload = () => {
      document.body.appendChild(img);
      
      // 显示提示
      const tip = document.createElement('div');
      tip.textContent = '长按图片保存到相册';
      tip.style.cssText = 'position: fixed; bottom: 50px; left: 50%; transform: translateX(-50%); background: rgba(0,0,0,0.7); color: white; padding: 12px 24px; border-radius: 20px; z-index: 10000; font-size: 14px;';
      document.body.appendChild(tip);

      // 点击图片或提示关闭
      const close = () => {
        document.body.removeChild(img);
        if (tip.parentNode) {
          document.body.removeChild(tip);
        }
      };
      
      img.addEventListener('click', close);
      tip.addEventListener('click', close);
      
      // 3秒后自动关闭提示
      setTimeout(() => {
        if (tip.parentNode) {
          document.body.removeChild(tip);
        }
      }, 3000);
    };
    img.onerror = () => {
      showError('图片加载失败，请稍后重试', 'CertificateView.saveImageToMobile');
    };
  };

  /**
   * 保存图片到桌面设备
   */
  const saveImageToDesktop = (imageUrl: string, filename: string): void => {
    const link = document.createElement('a');
    link.download = filename;
    link.href = imageUrl;
    link.click();
  };

  const handleDownload = async () => {
    if (!certificateRef.current) {
      showError('证书内容未加载', 'CertificateView.handleDownload');
      return;
    }

    try {
      // 动态导入 html2canvas
      let html2canvas;
      try {
        html2canvas = (await import('html2canvas')).default;
      } catch (importError) {
        showError('html2canvas 未安装，请运行: npm install html2canvas', 'CertificateView.handleDownload');
        return;
      }
      
      const canvas = await html2canvas(certificateRef.current, {
        backgroundColor: null,
        scale: 2, // 提高清晰度
        useCORS: true,
        logging: false,
      });

      const imageUrl = canvas.toDataURL('image/png');
      const filename = `捐赠证书_${donation?.donationOrderNo || donationId}.png`;

      // 根据设备类型选择保存方式
      if (isMobile()) {
        // 移动端：引导用户长按保存
        await saveImageToMobile(imageUrl);
      } else {
        // 桌面端：直接下载
        saveImageToDesktop(imageUrl, filename);
      }
    } catch (error) {
      logError('[CertificateView] Failed to download certificate', error);
      showError('下载证书失败，请稍后重试', 'CertificateView.handleDownload');
    }
  };

  if (loading) {
    return (
      <div className="certificate-view">
        <TopBar title="证书页" showBack onBack={onBack} />
        <div className="certificate-loading">加载中...</div>
      </div>
    );
  }

  const currentOrg = organization || contextOrg;
  
  if (!donation || !currentOrg) {
    return (
      <div className="certificate-view">
        <TopBar title="证书页" showBack onBack={onBack} />
        <div className="certificate-error">证书数据加载失败</div>
      </div>
    );
  }

  const salutation = currentOrg.certSalutation || '亲爱的';
  const donationName = donorProfile?.name || donation.donorNickname || donation.donorName || '爱心人士';
  const donationTime = donation.paymentTime
    ? formatFullDateTime(donation.paymentTime)
    : formatFullDateTime(donation.createTime);
  const dateStr = donationTime.split(' ')[0]; // 只取日期部分
  const year = dateStr.split('-')[0];
  const month = dateStr.split('-')[1];
  const day = dateStr.split('-')[2];

  return (
    <div className="certificate-view">
      <TopBar title="证书页" showBack onBack={onBack} />
      <div className="certificate-container">
        <div
          ref={certificateRef}
          className="certificate-content"
          style={{
            backgroundImage: currentOrg.certBackgroundImageUrl
              ? `url(${getImageUrl(currentOrg.certBackgroundImageUrl)})`
              : 'none',
          }}
        >
          <div className="certificate-inner">
            <div className="certificate-salutation">
              {salutation}「{donationName}」
            </div>
            <div
              className="certificate-text"
              dangerouslySetInnerHTML={{ __html: certificateContent }}
            />
            <div className="certificate-footer">
              <div className="certificate-org-name">{currentOrg?.name || '机构'}</div>
              <div className="certificate-date">
                {year}年{month}月{day}日
              </div>
            </div>
          </div>
        </div>
        <div className="certificate-actions">
          <button className="certificate-download-btn" onClick={handleDownload}>
            下载证书
          </button>
          <div className="certificate-hint">长按图片保存</div>
        </div>
      </div>
    </div>
  );
};

